// composables/useSchedule.ts or utils/schedule.ts (choose where it fits best)
import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";

export function useSchedule() {
  const { t } = useI18n();

  // reactive store for fetched schedule
  const raw = ref<any[]>([]);
  const pending = ref(false);
  const error = ref<any>(null);

  // Fetch schedule from the API (runs on server during SSR and on client later)
  watchEffect(async (onInvalidate) => {
    pending.value = true;
    error.value = null;

    let cancelled = false;
    onInvalidate(() => (cancelled = true));

    try {
      const res = await $fetch("/api/schedule");
      if (!cancelled) raw.value = Array.isArray(res) ? res : [];
    } catch (e) {
      console.error("Failed to fetch schedule:", e);
      if (!cancelled) {
        raw.value = [];
        error.value = e;
      }
    } finally {
      if (!cancelled) pending.value = false;
    }
  });

  // Format fetched rows into the simple map expected by Contacts.vue
  const schedule = computed<Record<string, string>>(() => {
    const days = {
      monday: [] as string[],
      tuesday: [] as string[],
      wednesday: [] as string[],
      thursday: [] as string[],
      friday: [] as string[],
      saturday: [] as string[],
      sunday: [] as string[],
    };

    // raw rows expected shape: { day: string, start_time?: string, end_time?: string, is_closed?: boolean }
    const closedDays = new Set<string>();
    for (const row of raw.value) {
      if (!row || !row.day) continue;
      const key = String(row.day).toLowerCase();
      if (!Object.prototype.hasOwnProperty.call(days, key)) continue;

      // If row explicitly marks the day as closed, record and skip ranges
      if (row.is_closed) {
        closedDays.add(key);
        continue;
      }

      // Legacy: ignore rows explicitly deactivated via `active = false` if present
      if (row.hasOwnProperty("active") && row.active === false) continue;

      // Normalize time strings (assume HH:MM or HH:MM:SS)
      const start = String(row.start_time || "").slice(0, 5);
      const end = String(row.end_time || "").slice(0, 5);
      if (!start || !end) continue;
      const range = `${start}–${end}`;

      // Prevent duplicate identical ranges
      if (!days[key].includes(range)) {
        days[key].push(range);
      }
    }

    const result: Record<string, string> = {};
    for (const [k, ranges] of Object.entries(days)) {
      if (closedDays.has(k)) {
        result[k] = t("contacts.closed");
        continue;
      }

      if (ranges.length === 0) {
        result[k] = t("contacts.closed");
      } else {
        // Deduplicate and sort ranges by start time
        const unique = Array.from(new Set(ranges));
        unique.sort((a, b) => {
          const [as] = a.split("–");
          const [bs] = b.split("–");
          const [ah, am] = as.split(":").map(Number);
          const [bh, bm] = bs.split(":").map(Number);
          return ah * 60 + am - (bh * 60 + bm);
        });
        result[k] = unique.join(", ");
      }
    }

    // If no data fetched at all, provide a sensible default fallback
    const allClosed = Object.values(result).every(
      (v) => v === t("contacts.closed")
    );
    if (raw.value.length === 0 && allClosed) {
      return {
        monday: "10:00–13:00, 14:00–20:00",
        tuesday: "10:00–13:00, 14:00–20:00",
        wednesday: "10:00–13:00, 14:00–20:00",
        thursday: "10:00–13:00, 14:00–20:00",
        friday: "10:00–13:00, 14:00–20:00",
        saturday: "10:00–13:00, 14:00–20:00",
        sunday: t("contacts.closed"),
      };
    }

    return result;
  });

  return {
    schedule,
    raw,
    pending,
    error,
  };
}
